/* Crave Me — ported from the comp's inline script.
 *
 * Two changes from the comp, both forced by Shopify:
 *
 * 1. Everything is inside an idempotent init(root). The theme editor swaps a
 *    section's HTML in without a page load, which silently kills every listener
 *    attached to the old nodes and leaves the rail parked at scrollLeft 0. So we
 *    re-init on shopify:section:load and mark what we have already wired.
 * 2. Every lookup is guarded. In the comp every page shared one shell, so .crew
 *    and .tags-rail always existed. As sections they are optional, and the comp's
 *    `crew.scrollWidth` would throw on any page without them.
 */
(function () {
  'use strict';

  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DONE = 'data-cm-init';


  /* close the menu panel when a link inside it is followed */
  function initMenu(root) {
    root.querySelectorAll('.mnav-panel a:not([' + DONE + '])').forEach(function (a) {
      a.setAttribute(DONE, '');
      a.addEventListener('click', function () {
        var d = a.closest('details');
        if (d) d.removeAttribute('open');
      });
    });
  }

  /* Shared wrap for both carousels: the track holds the set twice, so jumping by
   * exactly one period is invisible. That is what lets them scroll past either
   * end in both directions instead of stopping at scrollLeft 0. */
  function wrapper(el) {
    var period = function () { return el.scrollWidth / 2; };
    return {
      period: period,
      wrap: function () {
        var p = period(), sl = el.scrollLeft;
        if (sl < p * 0.5) el.scrollLeft = sl + p;
        else if (sl > p * 1.5) el.scrollLeft = sl - p;
      }
    };
  }

  /* #crumbnation — manual only, arrows step two poses */
  function initCrew(root) {
    var crew = root.querySelector('.crew:not([' + DONE + '])');
    if (!crew) return;
    crew.setAttribute(DONE, '');

    var w = wrapper(crew);
    crew.scrollLeft = w.period();
    crew.addEventListener('scroll', w.wrap, { passive: true });

    /* two poses per click, not 80% of the viewport -- that read as a jump.
     * Measured off a real pose so it stays correct as the type scales. */
    function step() {
      var pose = crew.querySelector('.pose');
      if (!pose) return crew.clientWidth * 0.3;
      return pose.getBoundingClientRect().width +
             parseFloat(getComputedStyle(pose).marginRight || 0);
    }

    var nav = crew.closest('.crumbnation') || root;
    nav.querySelectorAll('.crew-nav button').forEach(function (b) {
      b.addEventListener('click', function () {
        crew.scrollBy({
          left: b.dataset.dir * step() * 2,
          behavior: reduce ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* Tag rail — auto-drifts, and stays hand-scrollable in both directions.
   *
   * The drift runs through scrollLeft, NOT a CSS transform. It used to be both
   * at once and that is what broke the loop: a transform does not move
   * scrollLeft, so the two offsets added and the wrap thresholds could not see
   * the transform to correct it. One transport only. Do not re-add an animation
   * to .tags-track. */
  function initRail(root) {
    var rail = root.querySelector('.tags-rail:not([' + DONE + '])');
    if (!rail) return;
    rail.setAttribute(DONE, '');

    var w = wrapper(rail);
    rail.scrollLeft = w.period();

    /* Held while the visitor is touching or reading it -- the same cases the old
     * animation-play-state rule covered via hover, active and focus.
     *
     * pointercancel is the important one. On a touchscreen, a finger that starts
     * on the rail and turns into a page scroll fires pointercancel, NOT pointerup:
     * without it here, `held` latched true on the first touch and the rail never
     * drifted again for the rest of the visit. That is what "it should be on auto"
     * turned out to mean.
     *
     * The watchdog is the belt to that braces, and only for touch. A mouse resting
     * on the rail should keep it paused for as long as it rests there, but a touch
     * that never sends a matching release must not stop the drift forever. */
    var held = false, heldTouch = false, heldAt = 0;
    function hold(e) {
      held = true;
      heldAt = performance.now();
      heldTouch = !!(e && e.pointerType && e.pointerType !== 'mouse');
    }
    function release() { held = false; }

    ['pointerenter', 'pointerdown', 'focusin'].forEach(function (e) {
      rail.addEventListener(e, hold);
    });
    ['pointerleave', 'pointerup', 'pointercancel', 'focusout',
     'touchend', 'touchcancel'].forEach(function (e) {
      rail.addEventListener(e, release);
    });

    if (reduce) return;

    /* One period per `data-speed` seconds, derived from the period so the speed
       holds at any width: a phone and a desktop take the same time to travel the
       same proportion of the rail. Set from the section so it can be tuned
       without touching this file.
     * Stops when the rail leaves the document: in the theme editor a section is
     * removed and replaced on every edit, and without this check each edit
     * leaves another rAF loop running against a detached node forever. */
    var seconds = parseFloat(rail.getAttribute('data-speed')) || 300;

    /* The position is accumulated in a float and only handed to the DOM rounded.
     *
     * Writing the increment straight onto scrollLeft looks correct and does
     * nothing on iOS. At 600s per pass the rail moves 18px a second, which is
     * 0.305px per frame at 60fps; Safari quantises scrollLeft to whole pixels, so
     * every frame wrote 0.305 and read back the same integer, and the fraction was
     * discarded 60 times a second. Measured on a real iPhone: 2057 animation
     * frames, 0.0px moved, in 34 seconds. Chrome keeps fractional scroll offsets,
     * which is why it drifted on desktop and was dead on the phone. No speed
     * setting would have saved it -- even the fastest is under a pixel per frame.
     *
     * `pos` is resynced from the element whenever a hold ends, so a hand-scroll
     * hands its new position back rather than being yanked to where the drift
     * thought it was. */
    var pos = rail.scrollLeft;
    var lastWritten = pos;
    var userScrolling = false;
    var idle = null;

    /* Yield to the hand.
     *
     * The drift writes scrollLeft every frame, so without this a flick is pinned
     * back the instant the finger lifts: iOS momentum keeps scrolling, the next
     * frame overwrites it, and the rail refuses to be scrolled at all. Holding
     * only on pointer events is not enough, because momentum continues long after
     * pointerup.
     *
     * A scroll this loop did not cause is taken as the visitor's, whoever or
     * whatever is driving it. The drift stands down, takes their position as its
     * own, and resumes a quarter second after the scrolling stops. Ours are told
     * apart by remembering the last value written and allowing 2px for rounding.
     */
    rail.addEventListener('scroll', function () {
      if (Math.abs(rail.scrollLeft - lastWritten) <= 2) return;
      w.wrap();
      pos = rail.scrollLeft;
      lastWritten = pos;
      userScrolling = true;
      clearTimeout(idle);
      idle = setTimeout(function () { userScrolling = false; }, 250);
    }, { passive: true });

    var last = null;
    (function drift(now) {
      if (!rail.isConnected) return;
      if (held && heldTouch && now - heldAt > 4000) held = false;
      if (last !== null && !held && !userScrolling) {
        var p = w.period();
        pos += p / seconds * (now - last) / 1000;
        if (pos < p * 0.5) pos += p;
        else if (pos > p * 1.5) pos -= p;
        rail.scrollLeft = pos;
        lastWritten = rail.scrollLeft;
      }
      last = now;
      requestAnimationFrame(drift);
    })(performance.now());
  }

  /* Add to cart without a page load.
   *
   * Progressive enhancement only. The markup is a real <form> posting to
   * /cart/add, so with JavaScript off, or before this file has run, or if the
   * request fails, the browser submits it and the visitor lands on the cart.
   * Nothing here is load-bearing -- it only removes the page load.
   */
  /* Subscribe vs one-time.
   *
   * The markup renders only when a subscription app has created selling plans,
   * so on a store with none of this file does nothing.
   *
   * The one thing that has to be right: a disabled control is not submitted.
   * Choosing one-time disables the selling_plan input, and that is what makes it
   * a one-time order. Left enabled -- which is how the block was first written,
   * before any plan existed to expose it -- the radio changed nothing on screen
   * and every order went through as a subscription.
   *
   * Prices are read off data attributes the section wrote from the app's own
   * allocations. Nothing is computed here; a percentage in the theme is how a
   * page ends up advertising a discount the checkout does not give. */
  function initBuyOptions(root) {
    root.querySelectorAll('.product-form:not([' + DONE + '])').forEach(function (f) {
      if (!f.querySelector('.buy-options')) return;
      f.setAttribute(DONE, '');

      var plan = f.querySelector('[data-selling-plan]');
      var wasEl = f.querySelector('[data-sub-was]');
      var nowEl = f.querySelector('[data-sub-now]');

      function subscribing() {
        var r = f.querySelector('input[name="purchase"]:checked');
        return !!r && r.value === 'subscribe';
      }

      function chosenPlan() {
        if (!plan) return null;
        if (plan.tagName === 'SELECT') return plan.options[plan.selectedIndex] || null;
        return plan;
      }

      function sync() {
        var on = subscribing();
        var opt = chosenPlan();
        if (plan) plan.disabled = !on;

        if (opt && nowEl) nowEl.textContent = opt.dataset.price || nowEl.textContent;
        if (opt && wasEl) {
          wasEl.textContent = opt.dataset.was || '';
          wasEl.hidden = !opt.dataset.was;
        }

        /* re-queried rather than held: the add-to-cart flash rewrites the
         * button, so a reference taken once goes stale after the first add. */
        var btn = f.querySelector('button.add');
        var total = f.querySelector('[data-buy-total]');
        if (btn && total) {
          total.textContent = (on && opt && opt.dataset.price) ? opt.dataset.price : btn.dataset.oncePrice;
        }

        /* The sticky bar is a shortcut to this form, not a second offer. It
         * carries whatever is chosen here, price and plan both. */
        document.querySelectorAll('[data-plan-sync]').forEach(function (i) {
          i.value = on && opt ? (opt.value || opt.getAttribute('value') || '') : '';
          i.disabled = !i.value;
        });
        document.querySelectorAll('[data-sb-price]').forEach(function (el) {
          el.textContent = (on && opt && opt.dataset.price) ? opt.dataset.price
                                                            : (btn ? btn.dataset.oncePrice : el.textContent);
        });
      }

      f.addEventListener('change', function (e) {
        if (e.target.name === 'purchase' || e.target === plan) sync();
      });
      sync();
    });
  }

  function initCartForms(root) {
    root.querySelectorAll('form[action$="/cart/add"]:not([' + DONE + '])').forEach(function (form) {
      form.setAttribute(DONE, '');

      form.addEventListener('submit', function (ev) {
        var button = form.querySelector('[type="submit"]');
        if (!button) return;                       /* let the browser handle it */

        ev.preventDefault();
        /* innerHTML, not textContent: the product button holds a <span> that the
         * buy box rewrites with the price, and restoring text would delete it. */
        var original = button.innerHTML;
        button.disabled = true;

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        })
          .then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.json();
          })
          .then(function () { return refreshCount(); })
          .then(function () {
            button.textContent = button.dataset.added || 'Added';
            setTimeout(function () {
              button.innerHTML = original;
              button.disabled = false;
            }, 1600);
          })
          .catch(function () {
            /* the network or the variant let us down -- fall back to the real
             * submit rather than leaving a dead button */
            button.disabled = false;
            form.submit();
          });
      });
    });
  }

  /* The badge is the only part of the page that has to change after an add. */
  function refreshCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('a[href$="/cart"]').forEach(function (a) {
          if (a.hasAttribute('aria-label')) {
            a.setAttribute('aria-label', 'Cart, ' + cart.item_count +
              (cart.item_count === 1 ? ' item' : ' items'));
          }
        });
      });
  }

  /* Posts rail — arrows step one card, and the row never ends.
   *
   * Same wrap as #crumbnation: the set is rendered twice, so jumping by exactly
   * one period is invisible and the row runs forever in both directions. The
   * period is scrollWidth / 2, which is only exact because the cards carry
   * margin-right rather than the track carrying gap. See the note on
   * .post-rail .post.
   *
   * The step is measured off a real card rather than a fraction of the viewport,
   * so it stays right as the cards resize.
   */
  function initPostRail(root) {
    root.querySelectorAll('.post-rail:not([' + DONE + '])').forEach(function (sec) {
      sec.setAttribute(DONE, '');
      var rail = sec.querySelector('.rail');
      if (!rail) return;

      var w = wrapper(rail);
      rail.scrollLeft = w.period();
      rail.addEventListener('scroll', w.wrap, { passive: true });

      function step() {
        var card = rail.querySelector('.post');
        if (!card) return rail.clientWidth * 0.5;
        var cs = getComputedStyle(card);
        return card.getBoundingClientRect().width + (parseFloat(cs.marginRight) || 0);
      }

      sec.querySelectorAll('.crew-nav button').forEach(function (b) {
        b.addEventListener('click', function () {
          rail.scrollBy({ left: b.dataset.dir * step(), behavior: reduce ? 'auto' : 'smooth' });
        });
      });
    });
  }

  /* Product gallery — six slots choose which stacked media item is shown.
   *
   * Everything is already in the DOM, so this only toggles a class. The video
   * is paused on the way out: a muted clip left running behind display:none
   * keeps decoding, and on a phone that is battery for nothing.
   */
  /* iOS paints a large play/pause and two ten-second skips across the middle of
   * an inline video and holds them for about three seconds after a tap. These
   * clips run seven and eight seconds, so nearly half of one plays behind its
   * own controls -- and the subject is dead centre, which is exactly where the
   * chrome lands.
   *
   * Replace them with a tap target and a 4px progress line. Play, pause, and
   * how far through it is: everything one of these clips has to offer.
   *
   * Coarse pointers only. A desktop bar is a thin strip that hides itself on
   * mouse-out and carries seeking, volume and fullscreen -- worth more there
   * than matching the phone, and it was never the thing in the way.
   *
   * `controls` is removed here rather than left out of the markup, so the
   * native player is what a browser running no JavaScript still gets. */
  var coarse = matchMedia('(hover: none) and (pointer: coarse)').matches;

  function customControls(w) {
    var v = w.querySelector('video');
    var tap = w.querySelector('.vid-tap');
    var fill = w.querySelector('.vid-bar > i');
    if (!v || !tap) return;

    v.removeAttribute('controls');
    w.classList.add('vid-custom');

    tap.addEventListener('click', function () {
      /* play() rejects if the browser refuses the gesture. Nothing to do about
       * it here -- the mark stays up and the next tap tries again -- but an
       * unhandled rejection in the console reads like a bug in the page. */
      if (v.paused) { var p = v.play(); if (p) p.catch(function () {}); }
      else v.pause();
    });

    v.addEventListener('play', function () {
      w.classList.add('is-playing');
      tap.setAttribute('aria-label', 'Pause video');
    });
    ['pause', 'ended'].forEach(function (ev) {
      v.addEventListener(ev, function () {
        w.classList.remove('is-playing');
        tap.setAttribute('aria-label', 'Play video');
      });
    });
    v.addEventListener('timeupdate', function () {
      if (fill && v.duration) fill.style.width = (v.currentTime / v.duration * 100) + '%';
    });
  }

  function initGallery(root) {
    root.querySelectorAll('.product-gallery:not([' + DONE + '])').forEach(function (g) {
      g.setAttribute(DONE, '');
      var items = g.querySelectorAll('.gm-item');
      var btns = g.querySelectorAll('.product-thumbs button');
      if (!items.length || !btns.length) return;

      /* Every media item is in the DOM from the first render, which is what makes
       * choosing a video play it rather than fetch it -- but it also means two
       * videos start pulling bytes on a page nobody has asked to watch. Off until
       * chosen, then auto. The poster frame still draws either way, so a slot
       * nobody has picked costs a thumbnail, not a video. */
      items.forEach(function (it) {
        it.querySelectorAll('video').forEach(function (v) {
          if (!it.classList.contains('is-on')) v.preload = 'none';
        });
      });

      if (coarse) g.querySelectorAll('.vid').forEach(customControls);

      /* `play` is only ever true when show() was reached from a click on a slot.
       * The gesture is what makes it allowed: called synchronously inside the
       * click handler the browser still counts it as user-initiated, so it
       * starts with sound rather than being blocked by the autoplay policy.
       *
       * Only from a slot. Nothing here runs on page load, and a video that
       * nobody has asked for still costs a poster frame and nothing else. */
      function show(i, play) {
        items.forEach(function (it) {
          var on = it.dataset.media === String(i);
          it.classList.toggle('is-on', on);
          it.querySelectorAll('video').forEach(function (v) {
            if (!on) { v.pause(); return; }
            v.preload = 'auto';
            if (!play) return;
            /* rejected when the browser refuses the gesture anyway. The poster
             * and the play mark are already on screen, so the fallback is the
             * state it is in -- but an unhandled rejection reads like a bug. */
            var pr = v.play();
            if (pr) pr.catch(function () {});
          });
        });
        btns.forEach(function (b) {
          b.setAttribute('aria-current', b.dataset.media === String(i) ? 'true' : 'false');
        });
      }

      btns.forEach(function (b) {
        b.addEventListener('click', function () {
          show(b.dataset.media, true);
          /* The slots sit below the frame, so on a phone the thing just chosen
           * is off the top of the screen. Bring the frame back into view.
           *
           * Only when it is actually out of view, and only upward: scrolling a
           * reader who can already see the frame is a jolt for nothing, and
           * scrolling down would push the slots they are using off the bottom.
           *
           * scroll-margin-top on .product-gallery would be cleaner, but the
           * fixed chips are the reason a bare scrollIntoView lands under them,
           * and that margin is already set for anchor jumps on section[id]. */
          var box = g.getBoundingClientRect();
          if (box.top < 0) {
            var chips = document.querySelector('.topbar-inner');
            var clear = chips ? chips.getBoundingClientRect().height + 12 : 100;
            scrollTo({
              top: scrollY + box.top - clear,
              behavior: reduce ? 'auto' : 'smooth'
            });
          }
        });
      });
    });
  }

  /* Sticky buy bar — appears once the ingredient statement is half read.
   *
   * Halfway through the ingredients rather than "when the button leaves the
   * screen": that box is what a reader came for, and the middle of it is where
   * they have decided. Above that point the real Add to cart is usually still
   * in view and a second one is noise.
   *
   * Measured against the viewport middle, not its top, so the bar does not
   * appear the instant the heading clears the chips.
   */
  function initStickyBuy(root) {
    root.querySelectorAll('[data-sticky-buy]:not([' + DONE + '])').forEach(function (bar) {
      bar.setAttribute(DONE, '');
      var box = document.querySelector('.product-detail');
      if (!box) return;

      var on = false, queued = false;

      function evaluate() {
        queued = false;
        var r = box.getBoundingClientRect();
        var want = (r.top + r.height / 2) < innerHeight / 2;
        if (want === on) return;
        on = want;
        if (on) {
          bar.hidden = false;
          /* one frame between display and the class, or the transition has no
           * start state to move from and the bar simply appears */
          requestAnimationFrame(function () { bar.classList.add('is-on'); });
        } else {
          bar.classList.remove('is-on');
          setTimeout(function () { if (!on) bar.hidden = true; }, 200);
        }
      }

      function onScroll() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(evaluate);
      }

      addEventListener('scroll', onScroll, { passive: true });
      addEventListener('resize', onScroll);
      evaluate();
    });
  }

  function init(root) {
    root = root || document;
    initMenu(root);
    initGallery(root);
    initStickyBuy(root);
    initCrew(root);
    initRail(root);
    initPostRail(root);
    initBuyOptions(root);
    initCartForms(root);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  /* theme editor: sections are re-injected without a page load */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
